import { FormEvent, useEffect, useRef, useState } from "react";
//---------------------------------------------------------------------
import { myAxios} from "src/components/common/functions";
import { Form, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";
import { useUploadStore } from "../main/uploadStore";
import { Link } from "react-router-dom";
import { Option, Question, Training } from "./types";
//---------------------------------------------------------------------
export default function TrainingEditForm({ keyword }: {keyword: string}){
    const navigate = useNavigate();
    const id = (useParams()?.id ?? "") as string;
    const [data, setData] = useState<Training>(useRouteLoaderData("trainingDetail") ?? {questions: [], mode: "", name: "", passScore: "", description: "saved", id });
    const upload = useUploadStore();
    const onSubmit = async(event: FormEvent) => {
        const form = (event.currentTarget as HTMLElement).closest("form")!;
        if(!form.reportValidity()) return;
        if(!data.questions.length){
            window.alert("There has to be at least one problem.");
            return;
        }
        for(let i = 0; i < data.questions.length; i++){
            if(!data.questions[i].options.length){
                window.alert(`There has to be at least one option at question ${i + 1}.`);
                return;
            }
        }
        const formData = new FormData();
        const file = new File([
            JSON.stringify({name: data.name, mode: data.mode, questions: data.questions, passScore: data.passScore, upload })
        ], data.name, { type: "text/plain" });
        formData.append("status", "completed");
        formData.append("fileId", id);
        formData.append("fileName", data.name);
        formData.append("fileType", file.type);
        formData.append("fileSize", String(file.size));
        formData.append("lastModifiedAt", (new Date(file.lastModified)).toISOString().slice(0, 19).replace('T', ' '));
        const preRes = await myAxios({ fileName: "trainings/updateTraining.php", formData: formData });
        if(!preRes) return;
        upload.setFiles([file]);
        upload.setIds([preRes.data.id]);
        upload.setUrls([preRes.data.url]);
        upload.setShow(true);
        upload.setOnResetCallback(() => {
            navigate("..");
        });
    }
    const onSave = async() => {
        const formData = new FormData();
        const file = new File([
            JSON.stringify({name: data.name, mode: data.mode, questions: data.questions, passScore: data.passScore})
        ], data.name, { type: "text/plain" });
        formData.append("status", "saved");
        formData.append("fileId", id);
        formData.append("fileName", data.name);
        formData.append("fileType", file.type);
        formData.append("fileSize", String(file.size));
        formData.append("lastModifiedAt", (new Date(file.lastModified)).toISOString().slice(0, 19).replace('T', ' '));
        const preRes = await myAxios({ fileName: "trainings/updateTraining.php", formData: formData });
        if(!preRes) return;
        upload.setFiles([file]);
        upload.setIds([preRes.data.id]);
        upload.setUrls([preRes.data.url]);
        upload.setShow(true);
        upload.setOnResetCallback(() => {
            navigate("..");
        });
    }
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if(inputRef.current){
            const input = inputRef.current;
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }, [])
    //--------------------------------------------------------------------------------------------
    return(
        <Form method = "post">
            <h1 children = {keyword}/>
            <div className = "my-2 d-flex flex-wrap gap-3 align-items-center">
                <span className = "blk trainingFieldGroup">
                    <span className = "fw-bold me-3">Training Name:</span>
                    <input ref = {inputRef} required className = "inline form-control me-3" value = {data.name} onChange = {e => {
                        setData({...data, name: e.currentTarget.value});
                    }}/>
                </span>
                <span className = "blk trainingFieldGroup">
                    <span className = "fw-bold me-3">Training Mode:</span>
                    <select required className = "inline form-control me-3" value = {data.mode} onChange = {e => {
                        setData({...data, mode: e.currentTarget.value});
                    }}>
                        <option value = "">Select</option>
                        <option>Practice</option>
                        <option>Quiz</option>
                    </select>
                </span>
                <span className = "blk trainingFieldGroup">
                    <span className = "fw-bold me-3">Pass Score:</span>
                    <input
                        type = "number"
                        step = "0.5"
                        min = "0"
                        max = {data.questions.length}
                        required
                        className = "inline form-control me-3"
                        value = {data.passScore}
                        onChange = {e => {
                            setData({...data, passScore: e.currentTarget.value});
                        }}
                    />
                </span>
            </div>
            {data.questions.map((question: Question, questionIndex: number) => 
            <div key = {questionIndex} className = "border rounded p-3 my-3">
                <div className = "mb-3">
                    <div className = "fw-bold me-3">{questionIndex + 1}. Question Title:</div>
                    <textarea
                        required
                        className = "form-control"
                        style = {{minWidth: "100%"}}
                        value = {question.name}
                        onChange = {e => {
                            const questions = [
                                ...data.questions.slice(0, questionIndex), 
                                {...question, name: e.currentTarget.value},
                                ...data.questions.slice(questionIndex + 1)
                            ];
                            setData({...data, questions });
                        }}
                    />
                </div>
                <div className = "mb-3">
                    <span className = "me-3">Multiple Choice</span>
                    <input style = {{width: 30, height: 30, verticalAlign: "middle"}} type = "checkbox" checked = {question.multi} onChange = {e => {
                        const questions = [
                            ...data.questions.slice(0, questionIndex), 
                            {...question, 
                                multi: e.currentTarget.checked, 
                                options: question.options.map(option => ({...option, answer: false})),
                                signleAnswer: ""
                            }, 
                            ...data.questions.slice(questionIndex + 1)
                        ];
                        setData({...data, questions });
                    }}/>
                </div>
                <div>
                    <div className = "border rounded p-3 my-2">
                        <div className = "fw-bold">Question Options:</div>
                        {question.options.map((option, optionIndex) => 
                        <div key = {optionIndex} className = "my-2 p-2 border rounded">
                            <span className = "blk">
                                <span className = "me-3">Name</span>
                                <input
                                    required
                                    className = "inline form-control me-3"
                                    value = {option.name}
                                    onChange = {e => {
                                        const questions = [
                                            ...data.questions.slice(0, questionIndex), 
                                            {
                                                ...question,
                                                options: [...question.options.slice(0, optionIndex), {...option, name: e.currentTarget.value} , ...question.options.slice(optionIndex + 1)]
                                            },
                                            ...data.questions.slice(questionIndex + 1)
                                        ];
                                        setData({ ...data, questions })
                                    }}
                                />
                            </span>
                            <span className = "blk">
                                <span className = "me-3">Comment</span>
                                <input
                                    className = "inline form-control me-3"
                                    value = {option.comment}
                                    onChange = {e => {
                                        const questions = [
                                            ...data.questions.slice(0, questionIndex), 
                                            {
                                                ...question,
                                                options: [...question.options.slice(0, optionIndex), {...option, comment: e.currentTarget.value} , ...question.options.slice(optionIndex + 1)]
                                            },
                                            ...data.questions.slice(questionIndex + 1)
                                        ];
                                        setData({...data, questions});
                                    }}
                                />
                            </span>
                            <span className = "blk">
                                <label htmlFor = {questionIndex + "answer"} className = "mb-0 me-3 d-inline">Answer</label>
                                <input 
                                    required = {question.multi?false:true}
                                    id = {questionIndex + "answer"}
                                    name = {question.name} 
                                    type = {question.multi?"checkbox":"radio"} 
                                    className = "me-3"
                                    style = {{width: 30, height: 30, verticalAlign: "middle"}} 
                                    checked = {option.answer || false} 
                                    value = {option.name} 
                                    onChange = {e => {
                                        const questions = [
                                            ...data.questions.slice(0, questionIndex), 
                                            {
                                                ...question,
                                                options: [...question.options.slice(0, optionIndex).map(opt0 => ({...opt0, answer: question.multi?opt0.answer:false})), {...option, 
                                                    answer: question.multi?e.currentTarget.checked:true
                                                } , ...question.options.slice(optionIndex + 1).map(opt1 => ({...opt1, answer: question.multi?opt1.answer:false}))],
                                                signleAnswer: e.currentTarget.value
                                            },
                                            ...data.questions.slice(questionIndex + 1)
                                        ];
                                        setData({ ...data, questions });
                                    }}
                                />
                            </span>
                            <button
                                type = "button"
                                className = "btn btn-danger trainingActionButton"
                                onClick = {() => {
                                    const questions = [
                                        ...data.questions.slice(0, questionIndex),
                                        {
                                            ...question,
                                            options: question.options.filter(everyOption => everyOption.id !== option.id)
                                        },
                                        ...data.questions.slice(questionIndex + 1)
                                    ];
                                    setData({ ...data, questions });
                                }}
                            >
                                Delete
                            </button>
                        </div>)}
                        <button type = "button" className = "btn btn-primary me-3 trainingActionButton" onClick = {() => {
                            const newOption = { id: crypto.randomUUID(), name: "", comment: "", selected: false, answer: false } as Option;
                            const questions = [
                                ...data.questions.slice(0, questionIndex), 
                                {
                                    ...question,
                                    options: [...question.options, newOption]
                                },
                                ...data.questions.slice(questionIndex + 1)
                            ]
                            setData({ ...data, questions });
                        }}>Add Option</button>
                    </div>
                    <button type = "button" className = "btn btn-danger trainingActionButton" onClick = {() => {
                        const questions = [...data.questions.filter(everyQuesiton => question.id !== everyQuesiton.id)];
                        setData({ ...data, questions });
                    }}>Delete Question</button>
                </div>
            </div>
            )}
            <div className = "my-3">
                <button type = "button" className = "btn btn-primary trainingActionButton" onClick = {() => {
                    const newQuestion = {id: crypto.randomUUID(), name: "", options: [], multi: false } as Question;
                    const questions = [
                        ...data.questions, newQuestion
                    ];
                    setData({...data, questions });
                }}>Add Question</button>
            </div>
            <div className = "mb-3 d-flex flex-wrap gap-2">
                <Link children = "Close" to = ".." className = "btn btn-secondary trainingActionButton"/>
                <button children = {"Savd"} onClick = {onSave} type = "button" className = "btn btn-success trainingActionButton"/>
                <button children = {"Submit"} onClick = {onSubmit} type = "button" className = "btn btn-primary trainingActionButton ms-md-auto"/>
            </div>
        </Form>
    )
}
